import { useCallback, useMemo, useState } from 'react';
import { useModalStore } from '@/shared/lib/modalStore';
import { ModalComponentWrapper } from '@/shared/components';
import { BankTransferForm } from './BankTransferForm';
import { BankTransferErrorContent } from './BankTransferErrorContent';
import { useDepositMethods } from '@/widgets';
import {
  useInvoiceHistory,
  BankTransferModalFooter,
  useInvoiceDetailsDialog,
  useInvoiceHistoryDialog,
  useDepositBankTransferFormLogic,
  type BankTransferFormValues,
} from '@/features/deposit-bank-transfer';

interface DepositBankTransferModalProps {
  onBack?: () => void;
  onClose?: () => void;
  onSuccess?: (invoiceId: string) => void;
  title?: string;
}

export function DepositBankTransferModal({
  onBack,
  title,
  onSuccess,
}: DepositBankTransferModalProps) {
  const { setMultipleState } = useModalStore();
  const [isInvoiceDetailsOpen, setIsInvoiceDetailsOpen] = useState(false);
  const { showBankTransferModal } = useDepositMethods();
  const { openInvoiceHistoryDialog } = useInvoiceHistoryDialog();
  const { openInvoiceDetailsDialog } = useInvoiceDetailsDialog();

  const { data: invoiceHistory, isLoading: isHistoryLoading } =
    useInvoiceHistory();

  const handleHistoryClick = useCallback(() => {
    openInvoiceHistoryDialog({
      onBack: showBankTransferModal,
      onClose: showBankTransferModal,
      invoices: invoiceHistory,
    });
  }, [invoiceHistory]);

  const invoicesCount = useMemo(() => {
    if (isHistoryLoading || !invoiceHistory) {
      return undefined;
    }

    return invoiceHistory.length > 0 ? invoiceHistory.length : undefined;
  }, [isHistoryLoading, invoiceHistory]);

  const {
    form,
    watchedValues,
    handleFormSubmit,
    currencyOptions,
    minAmount,
    filteredCountries,
    bankOptions,
    commissionData,
    shouldShowCommission,
    isSubmitInProgressWithoutErrors,
    formError,
    isLoading,
    isCurrenciesFetching,
    isBanksFetching,
    isDependentListFetching,
    listsError,
    firstError,
  } = useDepositBankTransferFormLogic({
    onSuccess: invoiceId => {
      setMultipleState({
        isLoading: false,
      });
      setIsInvoiceDetailsOpen(true);
      onSuccess?.(invoiceId);
      openInvoiceDetailsDialog({
        mode: 'new',
        invoiceData: {
          invoiceId,
          bankKey: watchedValues.bankKey,
        },
        onBack: () => {
          setIsInvoiceDetailsOpen(false);
          showBankTransferModal();
        },
      });
    },
  });

  const submitForm = () => {
    form.handleSubmit((data: BankTransferFormValues) => {
      handleFormSubmit(data);
    })();
  };

  if (listsError) {
    return (
      <ModalComponentWrapper title={title} onBack={onBack}>
        <BankTransferErrorContent />
      </ModalComponentWrapper>
    );
  }

  return (
    <ModalComponentWrapper
      title={title}
      onBack={onBack}
      footer={
        <BankTransferModalFooter
          isHistoryLoading={isHistoryLoading}
          onHistoryClick={handleHistoryClick}
          invoicesCount={invoicesCount}
          onSubmit={submitForm}
        />
      }>
      <BankTransferForm
        form={form}
        watchedValues={watchedValues}
        handleFormSubmit={handleFormSubmit}
        currencyOptions={currencyOptions}
        minAmount={minAmount}
        filteredCountries={filteredCountries}
        bankOptions={bankOptions}
        commissionData={commissionData}
        shouldShowCommission={!!shouldShowCommission}
        isSubmitInProgressWithoutErrors={isSubmitInProgressWithoutErrors}
        formError={formError?.message}
        isLoading={isLoading}
        isCurrenciesFetching={isCurrenciesFetching}
        isBanksFetching={isBanksFetching}
        isDependentListFetching={isDependentListFetching}
        firstError={firstError?.message}
        isInvoiceDetailsOpen={isInvoiceDetailsOpen}
      />
    </ModalComponentWrapper>
  );
}
